import {Injectable} from '@angular/core';
import {
  BlackoutDay,
  BlackoutRange,
  DayType,
  ItemFilter,
  Restaurant,
  Time,
  TimeRange,
  WorkDay,
  WorkTime
} from '../models/restaurant.model';
import {DatePipe, LowerCasePipe} from '@angular/common';
import * as moment from 'moment';
import { DailyAvailability } from '../dto/catalog.dto';

@Injectable({
  providedIn: 'root'
})
export class FormatDataService {

  constructor(
    private lowerCasePipe: LowerCasePipe,
    private datePipe: DatePipe,
    ) {
  }

  public availableFromUntilText(availableFrom: Date, availableUntil: Date, includeTime: boolean): string {
    if (availableFrom && availableUntil) {
      if (this.getFormattedDate(availableFrom, includeTime) === this.getFormattedDate(availableUntil, includeTime)) {
        return `This item is only available on ${this.getFormattedDate(availableFrom, includeTime)}.`;
      }
      return `This item is only available from ${this.getFormattedDate(availableFrom, includeTime)} until ${this.getFormattedDate(availableUntil, includeTime)}.`;
    } else {
      return '';
    }
  }

  public getIntervalFromDuration(duration: string): number {
    return Number(moment.duration(duration).asMinutes());
  }

  public getIntervalFromDurationMili(duration: string): number {
    return Number(moment.duration(duration).asMilliseconds());
  }

  public getFormattedPrice(price: string | number, quantity = 1): string {
    return (+price * quantity).toFixed(2);
  }

  public getFormattedDate(date: Date, includeTime: boolean): string {
    let formattedDate = `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
    formattedDate += includeTime ? ` ${date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : '';
    return formattedDate;
  }

  public getStringInTitleCase(str: string): string {
    return str.toLowerCase().split(' ').map(function(word) {
      return (word.charAt(0).toUpperCase() + word.slice(1));
    }).join(' ');
  }

  public formatDurationToTimeString(value: string): string {
    const time: moment.Duration = moment.duration(value);
    if (time.days() !== 0) {
      return `${time.days()} days`;
    } else if (time.hours() !== 0) {
      return `${time.hours()} hours`;
    } else if (time.minutes() !== 0) {
      return `${time.minutes()} minutes`;
    }
    return '';
  }

  public formatDateToTimeString(date: Date): string {
    if (date.getHours() === 0 && date.getMinutes() === 0) {
      return 'midnight';
    } else if (date.getHours() === 12 && date.getMinutes() === 0) {
      return 'noon';
    } else {
      return this.datePipe.transform(date, 'h:mma').toLowerCase();
    }
  }

  public containsKeyword(name: string, keywordType: string): boolean {
    if (!name) {
      return false;
    }
    const formattedName: string = name.toLowerCase();
    switch (keywordType) {
      case ItemFilter.CONTAINS_ALCOHOL:
        return formattedName.includes('(21+)');
      case 'SELECTED':
        return formattedName.includes('(*)') || formattedName.includes('(#)');
      case 'HIDDEN':
        return formattedName.includes('(*)') || formattedName.includes('(^)');
      case 'SLIDER':
        return formattedName.includes('{s}');
      default:
        return false;
    }
  }

  public getFormattedAvailableFulfillments(availableInFulfillments: string): string {
    const fulfillments = ['Pickup', 'Curbside', 'Tableside', 'Tabs', 'Delivery', 'X-Side'];
    const variationAvailableFulfillments = availableInFulfillments.split(',');
    const availableFulfillmentsList: string[] = [];
    for (const fulfillment of variationAvailableFulfillments) {
      if (fulfillments.find((result: string) => { return fulfillment === result; })) {
        availableFulfillmentsList.push(fulfillment);
      }
    }
    return this.buildCommaListWithAnd(availableFulfillmentsList);
  }

  private workTimeToMinutes(t?: WorkTime | null): number | null {
    if (!t || t.time == null || t.time.hour == null || t.time.minute == null) return null;
    const isAM = t.meridian === true;
    const h12 = t.time.hour === 12 ? 0 : t.time.hour; // 12AM/12PM -> 0
    const hours24 = h12 + (isAM ? 0 : 12);
    return hours24 * 60 + t.time.minute;
  }

  private workTimeToLabel(t?: WorkTime | null): string | null {
    const mins = this.workTimeToMinutes(t);
    if (mins == null) return null;
    if (mins === 0) return 'midnight';
    if (mins === 12 * 60) return 'noon';

    const hours24 = Math.floor(mins / 60);
    const minutes = mins % 60;
    const ampm = hours24 < 12 ? 'am' : 'pm';
    let h = hours24 % 12;
    if (h === 0) h = 12;
    const mm = minutes.toString().padStart(2, '0');
    return `${h}:${mm}${ampm}`;
  }

  private isAllDay(startMins: number | null, endMins: number | null): boolean {
    return startMins === 0 && endMins === (23 * 60 + 59);
  }

  private buildCommaListWithAnd(list: string[]): string {
    if (!list || list.length === 0) return '';
    if (list.length === 1) return list[0];
    if (list.length === 2) return `${list[0]} and ${list[1]}`;
    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
  }

  /** ===== Main formatter ===== */
  public getFormattedDailyAvailabilityText(dailyAvailability: DailyAvailability[]): string {
    const textList: string[] = [];
    const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (!dailyAvailability || dailyAvailability.length === 0) {
      return '';
    }

    for (const day of dailyAvailability) {
      if (!day?.enabled) continue;

      let text = `${weekday[day.value ?? 0]}`;

      const startMins = this.workTimeToMinutes(day.startTime);
      const endMins = this.workTimeToMinutes(day.endTime);
      const startLabel = this.workTimeToLabel(day.startTime);
      const endLabel = this.workTimeToLabel(day.endTime);

      if (startMins == null || endMins == null) {
        textList.push(text);
        continue;
      }

      if (this.isAllDay(startMins, endMins)) {
        textList.push(text);
        continue;
      }

      if (startMins === 0) {
        text += ` (until ${endLabel})`;
      } else if (endMins === (23 * 60 + 59)) {
        text += ` (after ${startLabel})`;
      } else {
        text += ` (${startLabel}-${endLabel})`;
      }

      textList.push(text);
    }

    return this.buildCommaListWithAnd(textList);
  }

  public formatWorkDay(workDay: WorkDay): String {
    if (!workDay) {
      return;
    }
    const {startTime, endTime} = workDay;
    let workDayString = '';
    if ((startTime[0].time.hour === 12 && startTime[0].time.minute === 0 && startTime[0].meridian) && (endTime[0].time.hour === 11 && endTime[0].time.minute === 59 && !endTime[0].meridian)) {
      return 'All day';
    }
    for (const [index] of startTime.entries()) {
      workDayString += `${this.formatTime(startTime[index]?.today)} - ${this.formatTime(endTime[index]?.today)}, `;
    }
    if (workDay.isBlackouted) {
      workDayString = workDayString.slice(0, -2);
      workDayString += ` (${workDay.blackoutDayName})`;
      return workDayString;
    }

    return workDayString.slice(0, -2);
  }

  public formatWeekDay(workDay: WorkDay) {
    const month = workDay.date.getMonth() + 1;
    const day = workDay.date.getDate();
    return `${workDay.dayType.dayType.replace(/(\w)(\w*)/g, (_, firstChar, rest) => firstChar + rest.slice(0, 2).toLowerCase())} (${month}/${day})`;
  }

  public daysCount(dayStart: Date, dayEnd: Date): number {
    const start = moment(dayStart.toISOString());
    const end = moment(dayEnd.toISOString());

    return end.diff(start, 'days');
  }

  public isSameDate(dateOne: Date, dateTwo: Date): boolean {
    return moment(dateOne.toISOString()).isSame(dateTwo.toISOString(), 'day');
  }

  public isAfterDate(dateOne: Date, dateTwo: Date): boolean {
    return moment(dateOne.toISOString()).isAfter(dateTwo.toISOString(), 'day');
  }

  public isBeforeDate(dateOne: Date, dateTwo: Date): boolean {
    return moment(dateOne.toISOString()).isBefore(dateTwo.toISOString(), 'day');
  }

  public isTimeNowInWorkRange(day: WorkDay): boolean {
    for (const [index] of day.startTime.entries()) {
      const startMeridian = day.startTime[index].meridian ? 0 : 12;
      const endMeridian = day.endTime[index].meridian ? 0 : 12;
      const startTime = day.startTime[index].time.hour === 12 ? 0 : day.startTime[0].time.hour;
      const endTime = day.endTime[index].time.hour === 12 ? 0 : day.endTime[0].time.hour;
      const dateNow = new Date();
      const minutesNow = dateNow.getHours() * 60 + dateNow.getHours();
      if ((startTime + startMeridian) * 60 + day.startTime[index].time.minute <= minutesNow &&
        (endTime + endMeridian) * 60 + day.endTime[index].time.minute >= minutesNow) {
        return true;
      }
    }
    return false;
  }

  public formatWorkHoursWithBlackout(hoursOfOperation: WorkDay[], blackoutDays: BlackoutDay[], dayFrom: Date): WorkDay[] {
    const restaurantWorkHours: WorkDay[] = this.deepWorkDaysCopy(hoursOfOperation);
    const blackoutRangesOnThisWeek: BlackoutRange[] = this.generateBlackoutRangesForWeek(blackoutDays, dayFrom);
    const restaurantWeekFromToday: WorkDay[] = this.formatRestaurantWeekByWeekDays(restaurantWorkHours, dayFrom);
    const dayNow = new Date(dayFrom);
    if (blackoutRangesOnThisWeek.length > 0) {
      for (const workTime of restaurantWeekFromToday) {
        if (workTime.enable) {
          const workTimesLength = workTime.startTime.length;
          let isChanged = false;
          for (const blackoutRange of blackoutRangesOnThisWeek) {
            for (const blackoutDate of blackoutRange.dates) {
              // If blackout day same as current time day
              if (this.isSameDate(dayNow, blackoutDate)) {
                workTime.blackoutDayName = blackoutRange.name;
                workTime.isBlackouted = true;
                // If blackout day start and end in one day
                if (this.isSameDate(blackoutRange.dates[0], blackoutRange.dates[1]) && !isChanged) {
                  if (workTimesLength === 1) {
                    this.setBlackoutForNonRandedDay(workTime, blackoutRange, 0, true);
                    isChanged = true;
                  } else if (workTimesLength > 1) {
                    this.setBlackoutForRandedDay(workTime, blackoutRange, 0, true);
                    isChanged = true;
                  }
                } else {
                  // If blackout day between end blackout day and start blackout day
                  if (moment(blackoutDate.toISOString()).isAfter(blackoutRange.dates[0].toISOString(), 'day') && moment(blackoutDate.toISOString()).isBefore(blackoutRange.dates[blackoutRange.dates.length - 1].toISOString(), 'day') && !isChanged) {
                    workTime.enable = false;
                  } else if (moment(blackoutDate.toISOString()).isBefore(blackoutRange.dates[0].toISOString(), 'day') || moment(blackoutDate.toISOString()).isAfter(blackoutRange.dates[blackoutRange.dates.length - 1].toISOString(), 'day')) {
                    continue;
                  // If blackout days start equals current date
                  } else if (this.isSameDate(blackoutDate, blackoutRange.dates[0]) && !isChanged) {
                    if (workTimesLength === 1) {
                      this.setBlackoutForNonRandedDay(workTime, blackoutRange, 0, false);
                      isChanged = true;
                    } else if (workTimesLength > 1) {
                      this.setBlackoutForRandedDay(workTime, blackoutRange, 0, false);
                      isChanged = true;
                    }
                  // If blackout days end equals current date
                  } else if (this.isSameDate(blackoutDate, blackoutRange.dates[blackoutRange.dates.length - 1]) && !isChanged) {
                    if (workTimesLength === 1) {
                      this.setBlackoutForNonRandedDay(workTime, blackoutRange, blackoutRange.dates.length - 1, false);
                      isChanged = true;
                    } else if (workTimesLength > 1) {
                      this.setBlackoutForRandedDay(workTime, blackoutRange, blackoutRange.dates.length - 1, false);
                      isChanged = true;
                    }
                  }
                }
              }
            }
          }
        }
        dayNow.setDate(dayNow.getDate() + 1);
      }
    }
    return restaurantWeekFromToday;
  }

  // Format data for cases when work continue on next day
  public formatIfStoreContinueWorking(hoursOfOperation: WorkDay[]): WorkDay[] {
    const restaurantWorkHours = this.deepWorkDaysCopy(hoursOfOperation);
    for (const [index, workTime] of restaurantWorkHours.entries()) {
      const dayTime = workTime.endTime[workTime.endTime.length - 1];
      if (workTime.enable && dayTime.time.hour === 11 && dayTime.time.minute === 59 && !dayTime.meridian) {
        const currentDay = restaurantWorkHours[index];
        const nextDayTime = restaurantWorkHours[index + 1];
        if (nextDayTime != null && nextDayTime.enable && nextDayTime.startTime.length > 1
          && (nextDayTime.startTime[0].time.hour === 12 && nextDayTime.startTime[0].time.minute === 0 && nextDayTime.startTime[0].meridian)
          && (currentDay.endTime[currentDay.endTime.length - 1].time.hour === 11 && currentDay.endTime[currentDay.endTime.length - 1].time.minute === 59 && !currentDay.endTime[currentDay.endTime.length - 1].meridian)) {
          workTime.endTime[workTime.endTime.length - 1] = restaurantWorkHours[index + 1].endTime[0];
          this.deleteWorkTimeRange(restaurantWorkHours[index + 1], 0, 1);
        }
      }
    }
    return restaurantWorkHours;
  }

  // Format week for correct order of week days
  public formatRestaurantWeekByWeekDays(restaurantWorkHours: WorkDay[], dayFrom: Date): WorkDay[] {
    const restaurantWeekFromToday: WorkDay[] = [];
    const dayNow = new Date(dayFrom);
    let countedDate = new Date(dayFrom);
    countedDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone shift when converted to UTC
    for (let i = dayNow.getDay(); i < 7; i++) {
      const dateForDay = new Date(countedDate);
      restaurantWorkHours[i].date = dateForDay;
      restaurantWeekFromToday.push(restaurantWorkHours[i]);
      countedDate = new Date(countedDate);
      countedDate.setDate(countedDate.getDate() + 1);
    }
    for (let i = 0; i < dayNow.getDay(); i++) {
      const dateForDay = new Date(countedDate);
      restaurantWorkHours[i].date = dateForDay;
      restaurantWeekFromToday.push(restaurantWorkHours[i]);
      countedDate = new Date(countedDate);
      countedDate.setDate(countedDate.getDate() + 1);
    }
    return restaurantWeekFromToday;
  }

  // Generate another work days for scheduled timepicker in Checkout
  public generateWorkTimeInAdvance(filteredHoursOfOperation: WorkDay[], restaurant: Restaurant, pickupDaysInAdvance: number, timeRange: TimeRange): WorkDay[] {
    const startDate = new Date();
    let filteredWorkHours = this.deepWorkDaysCopy(filteredHoursOfOperation);
    for (let i = 7; i < pickupDaysInAdvance; i += 7) {
      startDate.setDate(startDate.getDate() + 7);
      filteredWorkHours = filteredWorkHours.concat(this.formatWorkHoursWithBlackout(restaurant.hoursOfOperation, restaurant.blackoutDateRanges, startDate));
    }
    if (timeRange != null) {
      filteredWorkHours = this.filterWithTimeRangeQueryParams(filteredWorkHours, timeRange);
    }
    return filteredWorkHours;
  }

  public filterWithTimeRangeQueryParams(workDays: WorkDay[], timeRange: TimeRange): WorkDay[] {
    const filteredWorkHours: WorkDay[] = [];
    for (const day of workDays) {
      if (this.isSameDate(day.date, timeRange.startTime)) {
        this.filterDayWithRange(day, timeRange.startTime, true);
      }
      if (this.isSameDate(day.date, timeRange.endTime)) {
        this.filterDayWithRange(day, timeRange.endTime, false);
      }
      if (this.isSameDate(day.date, timeRange.startTime) || this.isSameDate(day.date, timeRange.endTime)) {
        filteredWorkHours.push(day);
      }
      if (this.isAfterDate(day.date, timeRange.startTime) && this.isBeforeDate(day.date, timeRange.endTime)) {
        filteredWorkHours.push(day);
      }
    }
    return filteredWorkHours;
  }

  public filterDayWithRange(day: WorkDay, range: Date, isStartRange: boolean): void {
    for (const [index] of day.startTime.entries()) {
      const startMeridian = day.startTime[index].meridian ? 0 : 12;
      const endMeridian = day.endTime[index].meridian ? 0 : 12;
      const startTime = day.startTime[index].time.hour === 12 ? 0 : day.startTime[0].time.hour;
      const endTime = day.endTime[index].time.hour === 12 ? 0 : day.endTime[0].time.hour;
      if ((startTime + startMeridian) * 60 + day.startTime[index].time.minute <= range.getHours() * 60 + range.getMinutes() &&
        (endTime + endMeridian) * 60 + day.endTime[index].time.minute >= range.getHours() * 60 + range.getMinutes()) {
        const changeTime = isStartRange ? day.startTime[index] : day.endTime[index];
        const workTime = this.formatToWorkTime(range);
        changeTime.meridian = workTime.meridian;
        changeTime.time.hour = workTime.time.hour;
        changeTime.time.minute = workTime.time.minute;
      }
    }
  }

  public generateGroupWorkTime(restaurantsWorkHoursDaysInAdvance: WorkDay[][]): WorkDay[] {
    const copiedWorkHoursInAdvance = restaurantsWorkHoursDaysInAdvance.map( (day) => this.deepWorkDaysCopy(day));
    const sortedWorkHoursByDaysInAdvance = copiedWorkHoursInAdvance.sort((a, b) => {
      if (a.length < b.length) { return 1;  }
      if (a.length > b.length) { return -1; }
      return 0;
    });
    const groupHours = this.deepWorkDaysCopy(sortedWorkHoursByDaysInAdvance[0]);

    for (const [index, restaurantHours] of sortedWorkHoursByDaysInAdvance.entries()) {
      if (index === 0) continue;
      this.concatWorkDays(groupHours, this.deepWorkDaysCopy(restaurantHours));
    }

    return groupHours;
  }

  public deepCopy<T>(source: T): T {
    return Array.isArray(source) ? source.map(item => this.deepCopy(item)) : source instanceof Date ? new Date(source.getTime()) : source && typeof source === 'object' ? Object.getOwnPropertyNames(source).reduce((o, prop) => {
      Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop)!);
      o[prop] = this.deepCopy((source as { [key: string]: any })[prop]);
      return o;
    }, Object.create(Object.getPrototypeOf(source))) : source as T;
  }

  // Generate all blackout ranges for week from date dayFrom
  private generateBlackoutRangesForWeek(blackoutDays: BlackoutDay[], dayFrom: Date): BlackoutRange[] {
    const restaurantBlackoutDates = [...blackoutDays];
    const blackoutRangesOnThisWeek: BlackoutRange[] = [];
    for (const date of restaurantBlackoutDates) {
      if (date.enable) {
        const blackoutDatesOnThisWeek: Date[] = [];
        const blackoutTimesOnThisWeek: WorkTime[] = [];
        const blackoutDaysCount = this.daysCount(date.startDate, date.endDate) + 1;
        const blackoutDayEnd = new Date(date.endDate);
        for (let i = 0; i < blackoutDaysCount; i++) {
          for (let j = 0; j < 8; j++) {
            const today = new Date(dayFrom);
            const blackoutDayStart = new Date(date.startDate);
            const todayDate = new Date(today.setDate(today.getDate() + j));
            const blackoutDateStart = new Date(blackoutDayStart.setDate(blackoutDayStart.getDate() + i));
            if (j === 0) {
              const yesterday = new Date(dayFrom);
              const yesterdayDate = new Date(yesterday.setDate(yesterday.getDate() - 1));
              if (this.isSameDate(yesterdayDate, blackoutDateStart) && (yesterday.getDate() === today.getDate() - 1)) {
                blackoutDatesOnThisWeek.push(blackoutDateStart);
                blackoutTimesOnThisWeek.push(this.formatToWorkTime(blackoutDateStart));
              }
            }
            if (this.isSameDate(todayDate, blackoutDateStart)) {
              blackoutDatesOnThisWeek.push(blackoutDateStart);
              blackoutTimesOnThisWeek.push(this.formatToWorkTime(blackoutDateStart));
            }
          }
        }
        if (blackoutDatesOnThisWeek.length !== 0 && this.isSameDate(blackoutDatesOnThisWeek[blackoutDatesOnThisWeek.length - 1], blackoutDayEnd)) {
          if (blackoutDatesOnThisWeek.length === 1) {
            blackoutDatesOnThisWeek.push(blackoutDayEnd);
            blackoutTimesOnThisWeek.push(this.formatToWorkTime(blackoutDayEnd));
          } else {
            blackoutDatesOnThisWeek[blackoutDatesOnThisWeek.length - 1] = blackoutDayEnd;
            blackoutTimesOnThisWeek[blackoutTimesOnThisWeek.length - 1] = this.formatToWorkTime(blackoutDayEnd);
          }
        } else if (blackoutDatesOnThisWeek.length === 1 && this.isSameDate(blackoutDayEnd, blackoutDatesOnThisWeek[0])) {
          const endDate = new Date(blackoutDatesOnThisWeek[0]);
          endDate.setHours(23);
          endDate.setMinutes(59);
          blackoutDatesOnThisWeek[1] = endDate;
          blackoutTimesOnThisWeek[1] = this.formatToWorkTime(endDate);
        } else if (blackoutDatesOnThisWeek.length !== 0 && !this.isSameDate(blackoutDatesOnThisWeek[blackoutDatesOnThisWeek.length - 1], blackoutDayEnd)) {
          blackoutDatesOnThisWeek.push(blackoutDayEnd);
          blackoutTimesOnThisWeek.push(this.formatToWorkTime(blackoutDayEnd));
        }
        blackoutRangesOnThisWeek.push(new BlackoutRange(date.id, date.locationId, date.name, blackoutDatesOnThisWeek, blackoutTimesOnThisWeek));
      }
    }
    return blackoutRangesOnThisWeek;
  }

  // Format week day with blackout range when time on this day is not ranged
  private setBlackoutForNonRandedDay(workTime: WorkDay, blackoutRange: BlackoutRange, index: number, isSingleBlackoutRange: boolean) {
    const rangeTimes: WorkTime[] = this.getRangeTimes(blackoutRange, index, isSingleBlackoutRange);
    const blackoutRangeMeridian = rangeTimes[0].meridian ? 0 : 12;
    const blackoutRangeSecondMeridian = rangeTimes[1].meridian ? 0 : 12;
    const rangeStartTime = rangeTimes[0].time.hour === 12 ? 0 : rangeTimes[0].time.hour;
    const rangeEndTime = rangeTimes[1].time.hour === 12 ? 0 : rangeTimes[1].time.hour;
    let workStartMeridian = workTime.startTime[0].meridian ? 0 : 12;
    let workEndMeridian = workTime.endTime[0].meridian ? 0 : 12;
    let startTime = workTime.startTime[0].time.hour === 12 ? 0 : workTime.startTime[0].time.hour;
    let endTime = workTime.endTime[0].time.hour === 12 ? 0 : workTime.endTime[0].time.hour;
    if (this.getRangeTimeOutOfWorkTime(workTime, rangeTimes)) {
      workTime.enable = false;
      return;
    }
    if (this.getRangeTimeFinishEarlierThanWorkTime(workTime, rangeTimes) || this.getRangeTimeStartAfterClosedWorkTime(workTime, rangeTimes)) {
      return;
    }
    if ((rangeStartTime + blackoutRangeMeridian > startTime + workStartMeridian ||
         rangeStartTime + blackoutRangeMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute >= workTime.startTime[0].time.minute) &&
        (rangeEndTime + blackoutRangeSecondMeridian < endTime + workEndMeridian ||
         rangeEndTime + blackoutRangeSecondMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute <= workTime.endTime[0].time.minute)) {
      this.setNewRange(workTime, rangeTimes, 0);
    } else if ((rangeStartTime + blackoutRangeMeridian > startTime + workStartMeridian ||
                rangeStartTime + blackoutRangeMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute >= workTime.startTime[0].time.minute) &&
               (rangeEndTime + blackoutRangeSecondMeridian > endTime + workEndMeridian ||
                rangeEndTime + blackoutRangeSecondMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute >= workTime.endTime[0].time.minute)) {
      workTime.endTime[0] = rangeTimes[0];
    } else {
      if ((rangeStartTime + blackoutRangeMeridian > startTime + workStartMeridian ||
           rangeStartTime + blackoutRangeMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute >= workTime.startTime[0].time.minute) &&
          (rangeStartTime + blackoutRangeMeridian < endTime + workEndMeridian ||
           rangeStartTime + blackoutRangeMeridian === endTime + workEndMeridian && rangeTimes[0].time.minute <= workTime.endTime[0].time.minute)) {
        workTime.endTime[1] = workTime.endTime[0];
        workTime.startTime[1] = rangeTimes[0];
        workTime.endTime[0] = rangeTimes[0];
        workStartMeridian = workTime.startTime[1].meridian ? 0 : 12;
        workEndMeridian = workTime.endTime[1].meridian ? 0 : 12;
        endTime = workTime.endTime[1].time.hour === 12 ? 0 : workTime.endTime[1].time.hour;
        startTime = workTime.startTime[1].time.hour === 12 ? 0 : workTime.startTime[1].time.hour;
        if ((rangeEndTime + blackoutRangeSecondMeridian < endTime + workEndMeridian ||
          rangeEndTime + blackoutRangeSecondMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute <= workTime.endTime[1].time.minute) &&
          (rangeEndTime + blackoutRangeSecondMeridian > startTime + workStartMeridian ||
            rangeEndTime + blackoutRangeSecondMeridian === startTime + workStartMeridian && rangeTimes[1].time.minute >= workTime.startTime[1].time.minute)) {
          workTime.startTime[1] = rangeTimes[1];
          if (workTime.endTime.length !== workTime.startTime.length) {
            workTime.startTime.splice(1, 1);
          } else {
            this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, 1);
            this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, 0);
          }
        } else {
          workTime.endTime[1] = rangeTimes[1];
          if (workTime.endTime.length !== workTime.startTime.length) {
            workTime.endTime.splice(1, 1);
          } else {
            this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, 1);
            this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, 0);
          }
        }
      } else if (rangeStartTime + blackoutRangeMeridian < startTime + workStartMeridian ||
        rangeStartTime + blackoutRangeMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute <= workTime.startTime[0].time.minute) {
        if ((rangeEndTime + blackoutRangeSecondMeridian < endTime + workEndMeridian ||
          rangeEndTime + blackoutRangeSecondMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute <= workTime.endTime[0].time.minute) &&
          (rangeEndTime + blackoutRangeSecondMeridian > startTime + workStartMeridian ||
            rangeEndTime + blackoutRangeSecondMeridian === startTime + workStartMeridian && rangeTimes[1].time.minute >= workTime.startTime[0].time.minute)) {
          workTime.startTime[0] = rangeTimes[1];
          this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, 0);
        } else {
          workTime.enable = false;
        }
      }
    }
    this.deleteFirstRangeIfIncorrect(workTime);
  }

  // Format week day with blackout range when time on this day is ranged
  private setBlackoutForRandedDay(workTime: WorkDay, blackoutRange: BlackoutRange, index: number, isSingleBlackoutRange: boolean) {
    const rangeTimes: WorkTime[] = this.getRangeTimes(blackoutRange, index, isSingleBlackoutRange);
    let startIndex = null;
    let endIndex = null;
    let deleteFrom = null;
    let deleteUntil = null;
    if (this.getRangeTimeOutOfWorkTime(workTime, rangeTimes)) {
      workTime.enable = false;
      return;
    }
    if (this.getRangeTimeFinishEarlierThanWorkTime(workTime, rangeTimes) || this.getRangeTimeStartAfterClosedWorkTime(workTime, rangeTimes)) {
      return;
    }
    for (const [i] of workTime.startTime.entries()) {
      const workStartMeridian = workTime.startTime[i].meridian ? 0 : 12;
      const workEndMeridian = workTime.endTime[i].meridian ? 0 : 12;
      const blackoutRangeStartMeridian = rangeTimes[0].meridian ? 0 : 12;
      const blackoutRangeEndMeridian = rangeTimes[1].meridian ? 0 : 12;
      const startTime = workTime.startTime[i].time.hour === 12 ? 0 : workTime.startTime[i].time.hour;
      const endTime = workTime.endTime[i].time.hour === 12 ? 0 : workTime.endTime[i].time.hour;
      const rangeStartTime = rangeTimes[0].time.hour === 12 ? 0 : rangeTimes[0].time.hour;
      const rangeEndTime = rangeTimes[1].time.hour === 12 ? 0 : rangeTimes[1].time.hour;
      if ((rangeStartTime + blackoutRangeStartMeridian >= startTime + workStartMeridian) && (rangeEndTime + blackoutRangeEndMeridian <= endTime + workEndMeridian)) {
        this.setNewRange(workTime, rangeTimes, i);
        break;
      } else if ((rangeStartTime + blackoutRangeStartMeridian > startTime + workStartMeridian ||
                  rangeStartTime + blackoutRangeStartMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute >= workTime.startTime[i].time.minute) &&
                 (rangeStartTime + blackoutRangeStartMeridian < endTime + workEndMeridian ||
                  rangeStartTime + blackoutRangeStartMeridian === endTime + workEndMeridian && rangeTimes[0].time.minute <= workTime.endTime[i].time.minute) &&
                  startIndex == null) {
        this.setNewRange(workTime, rangeTimes, i);
        startIndex = i;
      } else if ((rangeStartTime + blackoutRangeStartMeridian < startTime + workStartMeridian ||
                  rangeStartTime + blackoutRangeStartMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute <= workTime.startTime[i].time.minute) &&
                  deleteFrom == null && startIndex == null) {
        deleteFrom = i;
      }
      if ((rangeEndTime + blackoutRangeEndMeridian > startTime + workStartMeridian ||
                  rangeEndTime + blackoutRangeEndMeridian === startTime + workStartMeridian && rangeTimes[1].time.minute >= workTime.startTime[i].time.minute) &&
                 (rangeEndTime + blackoutRangeEndMeridian < endTime + workEndMeridian ||
                  rangeEndTime + blackoutRangeEndMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute <= workTime.endTime[i].time.minute)) {
        workTime.startTime[i] = rangeTimes[1];
        endIndex = i;
      } else if (rangeEndTime + blackoutRangeEndMeridian < startTime + workStartMeridian ||
                 rangeEndTime + blackoutRangeEndMeridian === startTime + workStartMeridian && rangeTimes[1].time.minute <= workTime.startTime[i].time.minute) {
        deleteUntil = i;
      } else if ((rangeEndTime + blackoutRangeEndMeridian > endTime + workEndMeridian ||
                  rangeEndTime + blackoutRangeEndMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute >= workTime.endTime[i].time.minute) &&
                  startIndex != null) {
        workTime.startTime[i] = rangeTimes[1];
        endIndex = i;
      }
    }
    if (deleteFrom != null && deleteUntil != null && endIndex == null) {
      this.deleteWorkTimeRange(workTime, deleteFrom, deleteUntil - deleteFrom);
    } else if (deleteFrom != null && endIndex != null) {
      this.deleteWorkTimeRange(workTime, deleteFrom, endIndex - deleteFrom);
    } else if (endIndex != null && startIndex > 0) {
      this.deleteWorkTimeRange(workTime, startIndex + 1, endIndex - startIndex - 1);
    } else if (endIndex != null && startIndex === 0) {
      this.deleteWorkTimeRange(workTime, startIndex, endIndex - startIndex);
    } else if (endIndex == null && deleteUntil == null && startIndex != null) {
      this.deleteWorkTimeRange(workTime, 0, startIndex);
    } else if (startIndex != null && deleteUntil != null) {
      this.deleteWorkTimeRange(workTime, startIndex, deleteUntil - startIndex);
    }
    for (const [i] of workTime.endTime.entries()) {
      this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, i);
      this.deleteFirstRangeIfIncorrect(workTime);
    }
  }

  private deleteFirstRangeIfIncorrect(workTime: WorkDay) {
    if (workTime.endTime.length > 1) {
      const endTimeFirst = workTime.endTime[0].time.hour === 12 ? 0 : workTime.endTime[0].time.hour;
      const endTimeFirstMeridian = workTime.endTime[0].meridian ? 0 : 12;
      const startTimeSecond = workTime.startTime[1].time.hour === 12 ? 0 : workTime.startTime[1].time.hour;
      const startTimeSecondMeridian = workTime.startTime[1].meridian ? 0 : 12;
      if (startTimeSecond + startTimeSecondMeridian <= endTimeFirst + endTimeFirstMeridian) {
        this.deleteWorkTimeRange(workTime, 0, 1);
      }
    }
  }

  private deleteRangeIfStartTimeMoreOrSameEndTime(workTime: WorkDay, index: number) {
    const startTime = workTime.startTime[index].time.hour === 12 ? 0 : workTime.startTime[index].time.hour;
    const endTime = workTime.endTime[index].time.hour === 12 ? 0 : workTime.endTime[index].time.hour;
    const workStartMeridian = workTime.startTime[index].meridian ? 0 : 12;
    const workEndMeridian = workTime.endTime[index].meridian ? 0 : 12;
    if (startTime + workStartMeridian > endTime + workEndMeridian ||
      startTime + workStartMeridian === endTime + workEndMeridian && workTime.startTime[index].time.minute >= workTime.endTime[index].time.minute) {
      if (workTime.endTime.length === 1 || workTime.startTime.length === 1) {
        workTime.enable = false;
      } else {
        this.deleteWorkTimeRange(workTime, index, 1);
      }
    }
  }

  private formatToWorkTime(dateTime: Date): WorkTime {
    let hours = dateTime.getHours();
    if (hours === 0 || hours === 12) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }
    const minutes = dateTime.getMinutes();

    return new WorkTime(new Time(hours, minutes), dateTime.getHours() < 12);
  }

  private deepWorkDaysCopy(workDays: WorkDay[]): WorkDay[] {
    const newArr = [];
    if (!workDays) {
      return newArr;
    }
    for (const item of workDays) {
      const copiedWorkDay = JSON.parse(JSON.stringify(item));
      const workDay = new WorkDay(copiedWorkDay.id, copiedWorkDay.locationId, copiedWorkDay.enable, new DayType(copiedWorkDay.dayType.dayType),
        this.deepWorkTimeCopy(copiedWorkDay.startTime), this.deepWorkTimeCopy(copiedWorkDay.endTime), copiedWorkDay.isBlackouted, copiedWorkDay.blackoutDayName, item.date);
      workDay.date = item.date;
      newArr.push(workDay);
    }
    return newArr;
  }

  private deepWorkTimeCopy(workTime: WorkTime[]): WorkTime[] {
    const newArr = [];
    for (const item of workTime) {
      newArr.push(new WorkTime(new Time(item.time.hour, item.time.minute), item.meridian));
    }
    return newArr;
  }

  private getRangeTimes(blackoutRange: BlackoutRange, index: number, isSingleBlackoutRange: boolean): WorkTime[] {
    let rangeTimes: WorkTime[] = [];
    if (isSingleBlackoutRange && moment(blackoutRange.dates[0].toISOString()).isSame(blackoutRange.dates[1].toISOString(), 'day')) {
      rangeTimes = blackoutRange.times;
    } else {
      if (index === 0) {
        rangeTimes.push(blackoutRange.times[0]);
        rangeTimes.push(new WorkTime(new Time(11, 59), false));
      } else {
        rangeTimes.push(new WorkTime(new Time(12, 0), true));
        rangeTimes.push(blackoutRange.times[index]);
      }
    }
    return rangeTimes;
  }

  private getRangeTimeOutOfWorkTime(workTime: WorkDay, rangeTimes: WorkTime[]): boolean {
    const lastIndex = workTime.startTime.length - 1;
    const workStartMeridian = workTime.startTime[0].meridian ? 0 : 12;
    const workEndMeridian = workTime.endTime[lastIndex].meridian ? 0 : 12;
    const blackoutRangeStartMeridian = rangeTimes[0].meridian ? 0 : 12;
    const blackoutRangeEndMeridian = rangeTimes[1].meridian ? 0 : 12;
    const startTime = workTime.startTime[0].time.hour === 12 ? 0 : workTime.startTime[0].time.hour;
    const endTime = workTime.endTime[lastIndex].time.hour === 12 ? 0 : workTime.endTime[lastIndex].time.hour;
    const rangeStartTime = rangeTimes[0].time.hour === 12 ? 0 : rangeTimes[0].time.hour;
    const rangeEndTime = rangeTimes[1].time.hour === 12 ? 0 : rangeTimes[1].time.hour;

    if ((rangeStartTime + blackoutRangeStartMeridian < startTime + workStartMeridian ||
         rangeStartTime + blackoutRangeStartMeridian === startTime + workStartMeridian && rangeTimes[0].time.minute <= workTime.startTime[0].time.minute) &&
        (rangeEndTime + blackoutRangeEndMeridian > endTime + workEndMeridian ||
         rangeEndTime + blackoutRangeEndMeridian === endTime + workEndMeridian && rangeTimes[1].time.minute >= workTime.endTime[lastIndex].time.minute)) {
      return true;
    }
    return false;
  }

  private getRangeTimeFinishEarlierThanWorkTime(workTime: WorkDay, rangeTimes: WorkTime[]): boolean {
    const startTime = workTime.startTime[0].time.hour === 12 ? 0 : workTime.startTime[0].time.hour;
    const workStartMeridian = workTime.startTime[0].meridian ? 0 : 12;
    const rangeEndTime = rangeTimes[1].time.hour === 12 ? 0 : rangeTimes[1].time.hour;
    const blackoutRangeEndMeridian = rangeTimes[1].meridian ? 0 : 12;

    if ((rangeEndTime + blackoutRangeEndMeridian < startTime + workStartMeridian ||
        rangeEndTime + blackoutRangeEndMeridian === startTime + workStartMeridian && rangeTimes[1].time.minute <= workTime.startTime[0].time.minute)) {
      return true;
    }
    return false;
  }

  private getRangeTimeStartAfterClosedWorkTime(workTime: WorkDay, rangeTimes: WorkTime[]): boolean {
    const lastIndex = workTime.startTime.length - 1;
    const endTime = workTime.endTime[lastIndex].time.hour === 12 ? 0 : workTime.endTime[lastIndex].time.hour;
    const workEndMeridian = workTime.endTime[lastIndex].meridian ? 0 : 12;
    const rangeStartTime = rangeTimes[0].time.hour === 12 ? 0 : rangeTimes[0].time.hour;
    const blackoutRangeStartMeridian = rangeTimes[0].meridian ? 0 : 12;

    if ((rangeStartTime + blackoutRangeStartMeridian > endTime + workEndMeridian ||
      rangeStartTime + blackoutRangeStartMeridian === endTime + workEndMeridian && rangeTimes[0].time.minute >= workTime.endTime[0].time.minute)) {
      return true;
    }
    return false;
  }

  private setNewRange(workTime: WorkDay, rangeTimes: WorkTime[], i: number): void {
    workTime.endTime.splice(i + 1, 0, workTime.endTime[i]);
    workTime.startTime.splice(i + 1, 0, rangeTimes[1]);
    workTime.endTime[i] = rangeTimes[0];
    this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, i + 1);
    this.deleteRangeIfStartTimeMoreOrSameEndTime(workTime, i);
  }

  private concatWorkDays(groupWorkHours: WorkDay[], restaurantWorkHours: WorkDay[]): void {
    for (const gWorkDay of groupWorkHours) {
      for (const rWorkDay of restaurantWorkHours) {
        if (moment(gWorkDay.date).isSame(rWorkDay.date, 'day') && rWorkDay.enable) {
          this.concatWorkHours(gWorkDay, rWorkDay);
        }
      }
    }
  }

  private concatWorkHours(groupWorkDay: WorkDay, restaurantWorkDay: WorkDay): void {
    if (!groupWorkDay.enable) {
      groupWorkDay.startTime = restaurantWorkDay.startTime;
      groupWorkDay.endTime = restaurantWorkDay.endTime;
      groupWorkDay.enable = true;
      return;
    }
    if (groupWorkDay.startTime[0].time.hour === 12 && groupWorkDay.startTime[0].time.minute === 0 && groupWorkDay.startTime[0].meridian === true &&
        groupWorkDay.endTime[0].time.hour === 11 && groupWorkDay.endTime[0].time.minute === 59 && groupWorkDay.endTime[0].meridian === false) {
      return;
    }
    for (const [index] of restaurantWorkDay.startTime.entries()) {
      let isConcat = false;
      const rStartWorkTime = this.getWorkTimeInMinutes(restaurantWorkDay.startTime[index]);
      const rEndWorkTime = this.getWorkTimeInMinutes(restaurantWorkDay.endTime[index]);
      for (const [groupIndex] of groupWorkDay.startTime.entries()) {
        const gStartWorkTime = this.getWorkTimeInMinutes(groupWorkDay.startTime[groupIndex]);
        const gEndWorkTime = this.getWorkTimeInMinutes(groupWorkDay.endTime[groupIndex]);
        if (isConcat) {
          break;
        }
        if (gStartWorkTime > rStartWorkTime && gEndWorkTime < rEndWorkTime) {
          groupWorkDay.startTime[groupIndex] = restaurantWorkDay.startTime[index];
          groupWorkDay.endTime[groupIndex] = restaurantWorkDay.endTime[index];
          isConcat = true;
        } else if (gStartWorkTime < rStartWorkTime && gEndWorkTime < rEndWorkTime && gEndWorkTime > rStartWorkTime) {
          groupWorkDay.endTime[groupIndex] = restaurantWorkDay.endTime[index];
          isConcat = true;
        } else if (gStartWorkTime > rStartWorkTime && gEndWorkTime > rEndWorkTime && gStartWorkTime < rEndWorkTime) {
          groupWorkDay.startTime[groupIndex] = restaurantWorkDay.startTime[index];
          isConcat = true;
        } else if (gStartWorkTime > rEndWorkTime) {
          groupWorkDay.startTime.splice(groupIndex, 0, restaurantWorkDay.startTime[index]);
          groupWorkDay.endTime.splice(groupIndex, 0, restaurantWorkDay.endTime[index]);
          isConcat = true;
        } else if (gEndWorkTime < rStartWorkTime) {
          groupWorkDay.startTime.splice(groupIndex + 1, 0, restaurantWorkDay.startTime[index]);
          groupWorkDay.endTime.splice(groupIndex + 1, 0, restaurantWorkDay.endTime[index]);
          isConcat = true;
        }
      }
    }
    this.checkIsTimeRangesCorrect(groupWorkDay);
  }

  private checkIsTimeRangesCorrect(groupWorkDay: WorkDay): void {
    if (groupWorkDay.startTime.length === 1) return;
    for (const [index] of groupWorkDay.startTime.entries()) {
      const startWorkTime = this.getWorkTimeInMinutes(groupWorkDay.startTime[index]);
      const endWorkTime = this.getWorkTimeInMinutes(groupWorkDay.endTime[index]);
      if (groupWorkDay.startTime[index + 1] != null) {
        const nextStartWorkTime = this.getWorkTimeInMinutes(groupWorkDay.startTime[index + 1]);
        const nextEndWorkTime = this.getWorkTimeInMinutes(groupWorkDay.endTime[index + 1]);
        if (endWorkTime > nextStartWorkTime && startWorkTime < nextStartWorkTime) {
          if (endWorkTime > nextEndWorkTime) {
            this.deleteWorkTimeRange(groupWorkDay, index + 1, 1);
          } else {
            groupWorkDay.endTime[index] = groupWorkDay.endTime[index + 1];
            this.deleteWorkTimeRange(groupWorkDay, index + 1, 1);
          }
        }
      }
    }
  }

  private getWorkTimeInMinutes(workTime: WorkTime): number {
    const meridian = workTime.meridian ? 0 : 12;
    const hours = workTime.time.hour === 12 ? 0 : workTime.time.hour;
    return (hours + meridian) * 60 + workTime.time.minute;
  }

  private deleteWorkTimeRange(workDay: WorkDay, index: number, count: number) {
    workDay.startTime.splice(index , count);
    workDay.endTime.splice(index, count);
  }

  private formatTime(time: Date): string {
    return this.lowerCasePipe.transform(this.datePipe.transform(time, 'h:mm a'));
  }
}
